/* When this definition runs without anybody asking, as the design draws it: one line saying the
 * cadence, a pill saying whether it is on, and the two actions somebody actually takes — pause or
 * resume, or start one at a sensible default. Choosing a different time is one click further, in the
 * same panel.
 *
 * **No catch-up, and the line does not pretend otherwise.** A scheduler that was down for three days
 * owes one run, not three; the next run is on hover so the line stays one line.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Cadence, RunSchedule } from '@/shared/api/generated/reconciliation';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useRunSchedule, useSaveRunSchedule } from '../api/governance';
import {
  isConfigured,
  scheduleBadge,
  scheduleProblemKey,
  TIMEZONES,
  WEEKDAY_KEYS,
  weekdaysFor,
  type ScheduleDraft,
} from '../model/governance';

const EVERY_DAY: readonly number[] = [0, 1, 2, 3, 4, 5, 6];

/** The design's one-click start: every day at 05:00, before a working day begins. */
const DAILY_DEFAULT: ScheduleDraft = {
  enabled: true,
  cadence: 'daily',
  localTime: '05:00',
  timezone: 'Asia/Kolkata',
  weekdays: EVERY_DAY,
};

function draftFrom(schedule: RunSchedule): ScheduleDraft {
  return {
    enabled: schedule.enabled,
    cadence: schedule.cadence,
    localTime: schedule.local_time,
    timezone: schedule.timezone,
    weekdays: schedule.weekdays,
  };
}

export function RunSchedulePanel({
  definitionId,
  canConfigure,
}: {
  readonly definitionId: string;
  readonly canConfigure: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const reading = useRunSchedule(definitionId);
  const save = useSaveRunSchedule(definitionId);
  const [draft, setDraft] = useState<ScheduleDraft | null>(null);

  const write = (next: ScheduleDraft, onDone?: () => void): void => {
    save.mutate(
      {
        enabled: next.enabled,
        cadence: next.cadence,
        local_time: next.localTime,
        timezone: next.timezone,
        weekdays: weekdaysFor(next),
      },
      onDone === undefined ? undefined : { onSuccess: onDone },
    );
  };

  const configured =
    reading.isSuccess && isConfigured(reading.data) ? reading.data : null;
  const badge = configured === null ? null : scheduleBadge(configured);
  const problemKey = draft === null ? null : scheduleProblemKey(draft);

  return (
    <section className="lq-card lq-rc__panel lq-rdf__panel">
      <div className="lq-rdf__panelHead">
        <h3 className="lq-rc__panelTitle lq-rdf__grow">
          {t('reconciliation.schedule.title')}
        </h3>
        {badge === null ? null : (
          <span className="lq-rdf__pill" data-tone={badge === 'enabled' ? 'ok' : 'muted'}>
            {t(`reconciliation.schedule.badge.${badge}`)}
          </span>
        )}
      </div>

      {reading.isPending && <SkeletonRows rows={2} columns={1} />}
      {reading.isError && (
        <Alert tone="error" title={t('reconciliation.schedule.failed')}>
          {reading.error.message}
        </Alert>
      )}

      {configured !== null ? (
        <>
          <p
            className="lq-rdf__line"
            title={
              configured.next_run_at === null
                ? undefined
                : t('reconciliation.schedule.nextAt', {
                    when: new Date(configured.next_run_at).toLocaleString(),
                  })
            }
          >
            {configured.describes}
          </p>
          {configured.suspended_until !== null ? (
            <p
              className="lq-rdf__warnLine"
              title={configured.suspension_reason ?? undefined}
            >
              {t('reconciliation.schedule.pausedUntil', {
                when: new Date(configured.suspended_until).toLocaleString(),
              })}
            </p>
          ) : null}
          {canConfigure && draft === null ? (
            <div className="lq-rdf__row">
              <Button
                size="compact"
                busy={save.isPending}
                onClick={() => {
                  write({ ...draftFrom(configured), enabled: !configured.enabled });
                }}
              >
                {t('reconciliation.schedule.toggle')}
              </Button>
              <button
                type="button"
                className="lq-linkbutton"
                onClick={() => {
                  setDraft(draftFrom(configured));
                }}
              >
                {t('reconciliation.schedule.change')}
              </button>
            </div>
          ) : null}
        </>
      ) : reading.isSuccess ? (
        <>
          <p className="lq-rdf__quiet">{t('reconciliation.schedule.onlyOnRequest')}</p>
          {canConfigure && draft === null ? (
            <div className="lq-rdf__row">
              <Button
                size="compact"
                busy={save.isPending}
                onClick={() => {
                  write(DAILY_DEFAULT);
                }}
              >
                {t('reconciliation.schedule.dailyDefault')}
              </Button>
              <button
                type="button"
                className="lq-linkbutton"
                onClick={() => {
                  setDraft(DAILY_DEFAULT);
                }}
              >
                {t('reconciliation.schedule.chooseTime')}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {save.isError && draft === null ? (
        <p className="lq-rdf__error" role="alert">
          {save.error.message}
        </p>
      ) : null}

      {draft !== null && (
        <div className="lq-rdf__editor">
          <div className="lq-rdf__fields">
            <label className="lq-rdf__field">
              {t('reconciliation.schedule.cadence')}
              <select
                className="lq-input"
                value={draft.cadence}
                onChange={(event) => {
                  const cadence = event.target.value as Cadence;
                  setDraft({
                    ...draft,
                    cadence,
                    weekdays: cadence === 'daily' ? EVERY_DAY : draft.weekdays,
                  });
                }}
              >
                <option value="daily">{t('reconciliation.cadence.daily')}</option>
                <option value="weekly">{t('reconciliation.cadence.weekly')}</option>
              </select>
            </label>
            <label className="lq-rdf__field">
              {t('reconciliation.schedule.localTime')}
              <input
                className="lq-input lq-mono lq-rdf__time"
                value={draft.localTime}
                placeholder="05:00"
                onChange={(event) => {
                  setDraft({ ...draft, localTime: event.target.value });
                }}
              />
            </label>
            <label className="lq-rdf__field">
              {t('reconciliation.schedule.timezone')}
              {/* A list rather than free text: an offset cannot express a daylight-saving change,
                  so the control must not be able to produce one. */}
              <select
                className="lq-input"
                value={draft.timezone}
                onChange={(event) => {
                  setDraft({ ...draft, timezone: event.target.value });
                }}
              >
                {TIMEZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {draft.cadence === 'weekly' && (
            <div
              className="lq-rdf__days"
              role="group"
              aria-label={t('reconciliation.schedule.weekdays')}
            >
              {WEEKDAY_KEYS.map((key, index) => (
                <button
                  key={key}
                  type="button"
                  className="lq-rdf__day"
                  aria-pressed={draft.weekdays.includes(index)}
                  onClick={() => {
                    setDraft({
                      ...draft,
                      weekdays: draft.weekdays.includes(index)
                        ? draft.weekdays.filter((day) => day !== index)
                        : [...draft.weekdays, index].sort((a, b) => a - b),
                    });
                  }}
                >
                  {t(key)}
                </button>
              ))}
            </div>
          )}
          {problemKey !== null && (
            <p className="lq-rdf__error" role="alert">
              {t(problemKey)}
            </p>
          )}
          {save.isError && (
            <p className="lq-rdf__error" role="alert">
              {save.error.message}
            </p>
          )}
          <div className="lq-rdf__row">
            <Button
              size="compact"
              onClick={() => {
                setDraft(null);
                save.reset();
              }}
            >
              {t('reconciliation.publish.cancel')}
            </Button>
            <Button
              size="compact"
              tone="primary"
              disabled={problemKey !== null}
              busy={save.isPending}
              onClick={() => {
                write({ ...draft, enabled: true }, () => {
                  setDraft(null);
                });
              }}
            >
              {t('reconciliation.schedule.save')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
